import type { Project } from "@/data/types";
import { v4 as uuidv4 } from "uuid";

export const projects: Project[] = [
  {
    id: "task-manager-rest-api",
    title: "Task Manager REST API",
    description:
      "A production-style RESTful Task Management API built with Spring Boot, Spring Data JPA, and Hibernate. This backend application demonstrates enterprise Java development practices with a layered architecture, DTO pattern, validation, pagination, sorting, searching, filtering, and custom database queries. It provides a complete CRUD API for managing tasks while following Spring Boot best practices.",
    technologies: [
      "Java 21",
      "Spring Boot 3",
      "Spring Web",
      "Spring Data JPA",
      "Hibernate",
      "H2 Database",
      "Maven",
      "Lombok",
      "Jakarta Validation",
      "REST API",
      "SQL",
    ],
    category: "Backend",
    difficulty: "Intermediate",
    demoUrl: "https://taskmanager-api-pte1.onrender.com/api/v1/tasks",
    codeUrl: "https://github.com/Lil-Code30/taskmanager-api",
    thumbnail: "/images/projects/task-manager-rest-api.png",
    status: "In Progress",
    features: [
      "Complete CRUD operations for task management",
      "RESTful API design following Spring Boot conventions",
      "Layered architecture with Controller, Service, and Repository separation",
      "DTO pattern for clean request and response handling",
      "Bean validation using Jakarta Validation",
      "Global exception handling and meaningful HTTP responses",
      "Pagination support for large datasets",
      "Dynamic sorting with configurable fields and directions",
      "Task search by title",
      "Filtering tasks by completion status",
      "Advanced search with multiple query parameters",
      "Custom JPQL queries using Spring Data JPA",
      "H2 in-memory database integration",
      "HTTP status code handling using ResponseEntity",
      "Clean enterprise backend project structure",
    ],
  },
  {
    id: uuidv4(),
    title: "AcadXP",
    description:
      "Transform your academic journey into an engaging, gamified experience! Track your progress, visualize your skills, and level up through your degree with this comprehensive academic progress tracker.",
    technologies: [
      "TypeScript",
      "Next.js",
      "TailwindCSS",
      "Node.js",
      "Express",
      "PostgreSQL",
      "Neon",
    ],
    category: "Full-Stack",
    difficulty: "Advanced",
    codeUrl: "https://github.com/acadxp/acadxp",
    demoUrl: "https://acadxp.vercel.app/",
    thumbnail: "/images/projects/acadxp.png",
    status: "Paused",
    features: [
      "User Authentication: Secure login and registration system.",
      "Academic Tracking: Monitor courses, grades, and credits.",
      "Gamification: Earn points, badges, and level up based on academic performance.",
      "Progress Visualization: Interactive charts and graphs to visualize academic progress.",
      "Responsive Design: Mobile-friendly interface for all devices.",
      "Notifications: Email reminders for upcoming deadlines and events.",
      "User Profiles: Manage personal information and preferences.",
      "Search and Filter: Quickly find courses and academic records.",
      "Comprehensive Documentation: Clear documentation for setup and usage.",
    ],
  },
  {
    id: "wheat-grain-classifier-wpf",
    title: "Wheat Grain Classifier",
    description:
      "A modern desktop application built with WPF and the MVVM architecture that classifies wheat grain varieties using the K-Nearest Neighbors (KNN) machine learning algorithm. The application enables users to predict grain classes from morphological features, run large-scale classification experiments with configurable parameters, visualize performance metrics, and persist experiment results in SQL Server. The project combines desktop development, data processing, and machine learning fundamentals within a clean, maintainable .NET architecture.",
    technologies: [
      "C#",
      ".NET 10",
      "WPF",
      "XAML",
      "MVVM",
      "Entity Framework Core",
      "SQL Server",
      "CSV Helper",
      "MahApps.Metro",
      "Newtonsoft.Json",
      "Machine Learning",
      "K-Nearest Neighbors (KNN)",
    ],
    category: "Desktop",
    difficulty: "Advanced",
    codeUrl:
      "https://github.com/Lil-Code30/inf1126207-tp-02-wheat-grain-classifier-wpfApp",
    thumbnail: "/images/projects/wheat-grain-classifier.png",
    status: "Completed",
    features: [
      "Machine learning implementation using the K-Nearest Neighbors (KNN) algorithm",
      "Predict wheat grain varieties from seven morphological characteristics",
      "Support for Euclidean and Manhattan distance metrics",
      "Interactive prediction interface built with WPF",
      "Experiment mode for evaluating multiple KNN configurations",
      "Accuracy calculation and performance evaluation",
      "Confusion matrix generation for model analysis",
      "CSV dataset import for training and testing data",
      "SQL Server integration for storing experiment history",
      "Entity Framework Core for database access",
      "Modern MVVM architecture with Data Binding and Commands",
      "Dynamic view navigation using DataTemplates",
      "Responsive desktop interface with MahApps.Metro styling",
      "Modular architecture separating Models, Views, ViewModels, Services, and Data Access",
      "Custom implementations of distance algorithms and QuickSort for neighbor selection",
    ],
  },
  {
    id: "inventory-management-system",
    title: "Inventory Management System",
    description:
      "A full-stack inventory management platform developed for a local food manufacturing company to streamline inventory tracking, supplier management, production planning, and customer orders. The system centralizes raw materials and finished products while providing reporting dashboards, production workflows, and inventory analytics through a modern web application backed by a relational database.",
    technologies: [
      "JavaScript",
      "HTML",
      "CSS",
      "REST API",
      "MySQL",
      "SQL",
      "Docker",
      "Git",
      "GitLab",
      "DrawDB",
    ],
    category: "Full-Stack",
    difficulty: "Advanced",
    codeUrl:
      "https://github.com/cs-bachelor-journey/inf15122_TS_systeme-de-gestion-d-inventaire",
    thumbnail: "/images/projects/inventory-management-system.png",
    status: "Completed",
    features: [
      "Raw material inventory management",
      "Finished product inventory tracking",
      "Supplier management with product associations",
      "Purchase order management",
      "Customer management system",
      "Sales order processing",
      "Recipe and production management",
      "Production scheduling and planning",
      "Inventory level monitoring",
      "Automatic inventory availability validation",
      "Production versus inventory comparison",
      "Sales and profitability reports",
      "Production cost analysis",
      "Raw material consumption analytics",
      "Relational database design with normalized schema",
      "RESTful API architecture",
      "Dockerized deployment",
      "Collaborative development using Git and Agile workflows",
    ],
  },
  {
    id: uuidv4(),
    title: "TicketFox",
    description:
      "Centralized Knowledge Base: A structured repository for IT support documentation, troubleshooting guides, and best practices.",
    technologies: ["TypeScript", "Next.js", "TailwindCSS"],
    category: "Full-Stack",
    difficulty: "Advanced",
    codeUrl: "https://github.com/Lil-Code30/ticketfox",
    thumbnail: "/images/projects/ticket-fox.png",
    status: "Paused",
    features: [
      "IT Support Documentation",
      "Troubleshooting Guides",
      "Best Practices Repository",
      "Searchable Knowledge Base",
      "User-Friendly Interface",
      "Responsive Design for All Devices",
    ],
  },
  {
    id: "intelligent-file-organization",
    title: "Intelligent File Organization",
    description:
      "A Linux automation project that organizes files into categorized directories based on their extensions using Bash scripting. The application automates file management by detecting file types, creating organized folder structures, generating summary reports, and supporting configurable extension mappings for reusable file organization across different directories.",
    technologies: [
      "Bash",
      "Linux",
      "Shell Scripting",
      "GNU Core Utilities",
      "File System",
      "CLI",
      "Unix",
    ],
    category: "CLI",
    difficulty: "Intermediate",
    codeUrl:
      "https://github.com/cs-bachelor-journey/inf14107-tp-02-intelligent-file-organization",
    thumbnail: "/images/projects/intelligent-file-organization.png",
    status: "Completed",
    features: [
      "Automatic file organization by extension",
      "Categorize images, documents, code, and other file types",
      "Generate organized directory structures dynamically",
      "Automatic creation of user-specific workspace folders",
      "Generate detailed file organization reports",
      "Display file statistics by category",
      "Support configurable extension mappings through an external configuration file",
      "Accept custom directories as input",
      "Directory scanning using Bash arrays and loops",
      "Extension detection using Bash parameter expansion",
      "Automated file copy and move operations",
      "Portable Linux shell script compatible with standard Unix environments",
      "Command-line interface with configurable execution",
    ],
  },
  {
    id: uuidv4(),
    title: "SideQuest RPG",
    description: "A Quest Generator for real-life goals and tasks",
    technologies: ["TypeScript", "Next.js", "TailwindCSS", "localStorage"],
    category: "Full-Stack",
    difficulty: "Advanced",
    codeUrl: "https://github.com/DripCode-Studio/sidequest",
    demoUrl: "https://sidequest-rpg.vercel.app/",
    thumbnail: "/images/projects/sidequest-rpg.png",
    status: "Completed",
    features: [
      "Generate quests based on user input and preferences.",
      "Create and manage a personal quest log.",
      "Daily quest suggestions to keep users engaged and motivated.",
      "Daily streak tracking to encourage consistent progress and reward dedication.",
    ],
  },
  {
    id: uuidv4(),
    title: "SimpleBudget",
    description:
      "Plan your money visually. SimpleBudget is a modern budgeting tool for tracking income, expenses, and savings with real-time charts and analytics.",
    technologies: ["TypeScript", "Next.js", "TailwindCSS", "localStorage"],
    category: "Full-Stack",
    difficulty: "Advanced",
    codeUrl: "https://github.com/DripCode-Studio/simplebudget",
    demoUrl: "https://simplebudget-sigma.vercel.app/",
    thumbnail: "/images/projects/simplebudget.png",
    status: "Completed",
    features: [
      "Create and manage budgets for different categories.",
      "Track expenses and income to stay on top of your finances.",
      "Visualize spending habits with charts and graphs.",
    ],
  },
  {
    id: uuidv4(),
    title: "CakeCraft - Cake Customization",
    description:
      "A responsive full-stack web application that allows customers to customize cakes, place online orders, and manage order history. Built with JavaScript, Bootstrap, and a lightweight Node.js backend using JSON-based data persistence.",
    technologies: [
      "HTML5",
      "CSS3",
      "JavaScript",
      "Bootstrap 5",
      "Font Awesome",
      "Node.js",
      "JSON",
    ],
    category: "Full-Stack",
    difficulty: "Intermediate",
    demoUrl: "https://cs-bachelor-journey.github.io/inf16107-tp-02-cake-craft/",
    codeUrl: "https://github.com/cs-bachelor-journey/inf16107-tp-02-cake-craft",
    thumbnail: "/images/projects/cakecraft.png",
    status: "Completed",
    features: [
      "Interactive cake customization with real-time visual updates using JavaScript.",
      "Responsive multi-page interface built with Bootstrap 5.",
      "Online ordering system with customer information and cake customization options.",
      "RESTful Node.js backend supporting GET, POST, and DELETE operations.",
      "Persistent order storage using a local JSON file without a database.",
      "Order history page displaying all customer orders in Bootstrap cards.",
      "Ability to delete existing orders from the order history.",
      "Optional dynamic pricing system based on selected cake ingredients and toppings.",
    ],
  },
  {
    id: uuidv4(),
    title: "UNO - Game",
    description:
      "A complete object-oriented implementation of the classic UNO card game developed in Java. The project demonstrates core OOP principles including inheritance, polymorphism, abstraction, encapsulation, and game state management through a modular architecture.",
    technologies: [
      "Java",
      "Object-Oriented Programming",
      "UML",
      "Collections Framework",
    ],
    category: "Desktop",
    difficulty: "Advanced",
    codeUrl:
      "https://github.com/cs-bachelor-journey/inf11107-project-01-uno-gamee",
    thumbnail: "/images/projects/uno-game.png",
    status: "Completed",
    features: [
      "Complete implementation of the classic UNO card game following official gameplay rules.",
      "Object-oriented architecture featuring abstraction, inheritance, polymorphism, and encapsulation.",
      "Dynamic game engine handling player turns, direction changes, and victory conditions.",
      "Support for numeric cards and special action cards including Skip, Reverse, Draw Two, Wild, and Wild Draw Four.",
      "Deck management with automatic shuffling, drawing, discard pile, and deck reconstruction.",
      "Compatibility validation ensuring only legal cards can be played each turn.",
      "Player hand management with drawing, playing cards, and UNO declaration logic.",
      "Designed from a comprehensive UML class diagram using enums and modular class relationships.",
    ],
  },
  {
    id: uuidv4(),
    title: "Reliable UDP Backup Server",
    description:
      "A reliable file transfer system built on top of UDP in Python, implementing custom transport-layer reliability features such as handshakes, acknowledgements, retransmissions, transfer resumption, and end-to-end integrity verification.",
    technologies: [
      "Python",
      "UDP",
      "Networking",
      "CRC32",
      "Conda",
      "INI Configuration",
    ],
    category: "Backend",
    difficulty: "Advanced",
    codeUrl:
      "https://github.com/cs-bachelor-journey/INF26207_TS_H2026_Les_Sockets",
    thumbnail: "/images/projects/reliable-udp-backup-server.png",
    status: "Completed",
    features: [
      "Custom reliable transport protocol built on top of UDP without using TCP.",
      "Implements a three-way handshake to establish reliable client-server communication.",
      "Reliable file transfer using acknowledgements (ACK), negative acknowledgements (NACK), and timeout-based retransmissions.",
      "Supports interrupted transfer recovery by resuming uploads from the last valid byte received.",
      "Segment-level CRC32 validation detects corrupted packets during transmission.",
      "End-to-end CRC32 checksum verification guarantees complete file integrity after transfer.",
      "Sliding window transmission with configurable MSS and window size for improved throughput.",
      "Configurable network simulation including packet loss, corruption rate, retransmission timeout, and retry limits.",
      "Command-line client supporting file uploads, server file listing, connection management, and transfer resumption.",
      "Automatic storage of uploaded files on the server with persistent backup management.",
    ],
  },
  {
    id: uuidv4(),
    title: "CS Bachelor Journey - Project Collection",
    description:
      "All my PRojects related to my CS Bachelor degree, showcasing my learning journey and practical applications of computer science concepts.",
    technologies: [
      "c#",
      "Java",
      "Python",
      "c++",
      "HTML",
      "CSS",
      "JavaScript",
      "React",
      "Next.js",
      "TailwindCSS",
      "Node.js",
      "Express",
      "MySQL",
    ],
    category: "Full-Stack",
    difficulty: "Advanced",
    codeUrl: "https://github.com/cs-bachelor-journey",
    demoUrl: "https://cs-bs-journey.vercel.app/",
    thumbnail: "/images/projects/cs-journey-projects.png",
    status: "In Progress",
    features: [
      "Comprehensive collection of projects from my CS Bachelor degree.",
      "Showcases practical applications of computer science concepts.",
      "Includes projects in various programming languages and technologies.",
      "Demonstrates growth and learning throughout the degree program.",
    ],
  },
  {
    id: "multi-threaded-file-downloader",
    title: "Multi-Threaded File Downloader",
    description:
      "A Java concurrent file downloading application that allows users to download multiple PDF files simultaneously using a thread pool. The project demonstrates advanced Java concurrency concepts including ExecutorService, parallel task execution, thread-safe collections, network streams, and file system operations.",
    technologies: [
      "Java",
      "Java Concurrency",
      "ExecutorService",
      "Thread Pool",
      "ConcurrentHashMap",
      "Java NIO",
      "File I/O",
      "HTTP Networking",
      "Streams",
    ],
    category: "CLI",
    difficulty: "Advanced",
    codeUrl: "https://github.com/Lil-Code30/Multi-threaded-File-Downloader",
    thumbnail: "/images/projects/multi-threaded-file-downloader.png",
    status: "Completed",
    features: [
      "Download multiple files concurrently",
      "Thread pool management using ExecutorService",
      "Parallel execution of download tasks",
      "Thread-safe file tracking with ConcurrentHashMap",
      "PDF URL validation before downloading",
      "Automatic download directory creation",
      "Network file streaming using InputStream",
      "File saving using Java NIO Files API",
      "Download time measurement and performance tracking",
      "Display downloaded file sizes",
      "Resource management with Java streams",
      "Console-based interactive user interface",
      "Demonstrates Java multithreading and synchronization concepts",
    ],
  },
  {
    id: "employee-management-system-cli",
    title: "Employee Management System CLI",
    description:
      "A Java-based command-line Employee Management System built with JDBC and a DAO architecture. This application provides a complete console interface for managing employees and departments while demonstrating core backend concepts such as database connectivity, CRUD operations, object-oriented design, SQL queries, and the Data Access Object pattern.",
    technologies: [
      "Java",
      "JDBC",
      "SQL",
      "MySQL",
      "DAO Pattern",
      "Object-Oriented Programming",
      "Java Collections",
      "Maven",
    ],
    category: "CLI",
    difficulty: "Intermediate",
    codeUrl: "https://github.com/Lil-Code30/EmployeeCRUD-CLI",
    thumbnail: "/images/projects/employee-management-system.png",
    status: "Completed",
    features: [
      "Interactive command-line user interface",
      "Employee management with CRUD operations",
      "Department management system",
      "JDBC database connection handling",
      "DAO pattern implementation for database operations",
      "Create and store employee records",
      "Retrieve employees by ID",
      "Retrieve all employees",
      "Search employees by name",
      "Filter employees by department",
      "Filter employees by salary range/value",
      "Sort employees by hiring date",
      "Delete employee records",
      "Department creation and listing",
      "Object-oriented entity modeling with Employee and Department classes",
      "Formatted console output using custom printer utilities",
      "Database-driven application architecture",
    ],
  },
  {
    id: uuidv4(),
    title: "Job Hunt",
    description:
      "A job search management project designed to help organize applications, track progress, and manage the overall job hunt workflow.",
    technologies: ["NextJS", "Firebase"],
    category: "Full-Stack",
    difficulty: "Intermediate",
    codeUrl: "https://github.com/Lil-Code30/job-hunt",
    thumbnail: "/images/projects/job-hunt.png",
    status: "Completed",
    features: [
      "Job application tracking.",
      "Organized pipeline for opportunities.",
      "Useful for managing search progress and notes.",
    ],
  },
  {
    id: uuidv4(),
    title: "Portfolio Personnel v5",
    description: "Portfolio x CV.",
    technologies: ["HTML", "Javascript", "CSS"],
    category: "Frontend",
    difficulty: "Beginner",
    codeUrl: "https://github.com/Lil-Code30/portfolio",
    demoUrl: "https://ismaelloko.vercel.app/",
    thumbnail: "/images/projects/porfolio.png",
    status: "Completed",
    features: [
      "A personal portfolio and CV website.",
      "Showcases skills, projects, and experience.",
      "Designed for simplicity and clarity.",
    ],
  },
  {
    id: uuidv4(),
    title: "Cine Verse",
    description:
      "Search for movies, add them to a personal watchlist, and remove them as desired. The movie data is fetched from The Movie Database (TMDB) API. Stored watchlist data in localStorage for persistence across sessions.",
    technologies: [
      "HTML",
      "CSS",
      "JavaScript",
      "React",
      "Vite",
      "TailwindCSS",
      "TMDB API",
      "localStorage",
    ],
    category: "Frontend",
    difficulty: "Intermediate",
    codeUrl: "https://github.com/Lil-Code30/cine-verse",
    demoUrl: "https://cine-verse-seven.vercel.app/",
    thumbnail: "/images/projects/cine-verse.png",
    status: "Completed",
    features: [
      "Search for movies using the TMDB API",
      "Add movies to a personal watchlist",
      "Remove movies from the watchlist",
      "Responsive design for all devices",
      "Clean and modern design",
      "Error handling and loading states",
    ],
  },
  {
    id: uuidv4(),
    title: "Timora – Event Planning",
    description:
      "Create, manage, and synchronize events with any External Calendar. Designed for students and professionals seeking simplified scheduling.",
    technologies: [
      "Next.js",
      "TypeScript",
      "TailwindCSS",
      "PostgreSQL",
      "External Calendar API",
      "OAuth 2.0",
    ],
    category: "Full-Stack",
    difficulty: "Advanced",
    codeUrl: "https://github.com/Lil-Code30/timora",
    thumbnail: "/images/projects/timora.png",
    status: "In Progress",
    features: [
      "Event Creation: Easily create and manage events.",
      "Event Synchronization: Sync events with External Calendar.",
      "User Authentication: Secure login and registration system.",
      "Responsive Design: Mobile-friendly interface for all devices.",
      "Notifications: Email reminders for upcoming events.",
      "User Profiles: Manage personal information and preferences.",
      "Search and Filter: Quickly find events by date or keyword.",
      "Comprehensive Documentation: Clear documentation for setup and usage.",
    ],
  },
  {
    id: uuidv4(),
    title: "NWS MCP Server",
    description:
      "A simple Model Context Protocol weather server that exposes tools for fetching live National Weather Service alerts and forecasts.",
    technologies: ["TypeScript", "MCP", "zod"],
    category: "Backend",
    difficulty: "Intermediate",
    codeUrl: "https://github.com/Lil-Code30/nws-mcp-server",
    demoUrl: "https://www.npmjs.com/package/unofficial-nmw-mcp-server",
    thumbnail: "/images/projects/nws-mcp-server.png",
    status: "Completed",
    features: [
      "Weather forecast tool.",
      "Weather alerts tool.",
      "Live NWS API integration.",
      "MCP server setup for Claude-style clients.",
    ],
  },
  {
    id: uuidv4(),
    title: "AfroChef",
    description:
      "Discover and share authentic African recipes with AfroChef! This platform celebrates the rich culinary heritage of Africa, allowing users to explore traditional dishes, share their own recipes, and connect with a community of food enthusiasts.",
    technologies: ["TypeScript", "Next.js", "supabase", "TailwindCSS"],
    category: "Full-Stack",
    difficulty: "Advanced",
    codeUrl: "https://github.com/DripCode-Studio/afro-chef",
    demoUrl: "https://afrochef.dripcodestudio.com/",
    thumbnail: "/images/projects/afrochef.png",
    status: "Completed",
    features: [
      "User Authentication: Secure login and registration system.",
      "Recipe Management: Add, edit, and delete recipes with detailed instructions and images.",
      "Search and Filter: Easily find recipes by ingredients, cuisine, or dietary preferences.",
      "Adaptive Recipes: Adjust ingredient quantities based on serving size.",
      "AI-Powered Suggestions: Get recipe recommendations based on your preferences and past interactions.",
    ],
  },
  {
    id: uuidv4(),
    title: "LedgerYear",
    description:
      "LedgerYear is a comprehensive personal finance management application designed to help users track their income, expenses, and savings goals. With an intuitive interface and powerful features, LedgerYear empowers users to take control of their financial health and make informed decisions.",
    technologies: ["TypeScript", "Next.js", "supabase", "TailwindCSS"],
    category: "Full-Stack",
    difficulty: "Advanced",
    codeUrl: "https://github.com/DripCode-Studio/ledger-year",
    demoUrl: "https://ledgeryear.dripcodestudio.com/",
    thumbnail: "/images/projects/ledgeryear.png",
    status: "Completed",
    features: [
      "User Authentication: Secure login and registration system.",
      "Expense Tracking: Add, edit, and delete income and expense entries with categories and tags.",
      "Budget Management: Set monthly budgets and track spending against them.",
      "Viewualization: Interactive charts and graphs to visualize financial data.",
    ],
  },
  {
    id: uuidv4(),
    title: "Portfolio Personnel v4",
    description:
      "This is the fourth version of my personal portfolio, built with modern web technologies. It showcases my skills, projects, and experience as a web developer.",
    technologies: ["HTML", "TypeScript", "NextJS", "TailwindCSS", "Shadcn/ui"],
    category: "Frontend",
    difficulty: "Beginner",
    codeUrl: "https://github.com/Lil-Code30/ismael-loko",
    thumbnail: "/images/projects/ismael-loko.png",
    status: "Completed",
    features: [
      "Responsive Design: The portfolio is fully responsive and works on all devices.",
      "Modern UI: The UI is built with Shadcn/ui components, providing a clean and modern look. ",
      "GitHub Contribution grid",
      "Component-Based Architecture: The codebase is organized into reusable components, making it easy to maintain and scale.",
      "Responsive Navigation: A responsive navigation bar that adapts to different screen sizes.",
    ],
  },
  {
    id: uuidv4(),
    title: "DevKit",
    description:
      "Your all-in-one developer resource hub. Discover, use, and learn with premium APIs, UI components, templates, tools, tutorials, libraries and more.",
    technologies: ["TypeScript", "Next.js", "supabase", "TailwindCSS"],
    category: "Full-Stack",
    difficulty: "Intermediate",
    codeUrl: "https://github.com/DevKit-Org",
    demoUrl: "https://devkit.dripcodestudio.com/",
    thumbnail: "/images/projects/devkit.png",
    status: "Archived",
    features: [
      "Find Resources: Browse and search a wide range of developer resources.",
      "Search and Filter: Easily find resources by category, technology, or popularity.",
      "Submit Resources: Contribute your own resources to the community.",
      "Responsive Design: Mobile-friendly interface for all devices.",
    ],
  },
  {
    id: uuidv4(),
    title: "Stocky",
    description:
      "An inventory management web application that helps users track, organize, and control their stock in real time. It provides features for adding, updating, and monitoring inventory levels, making it easier to manage stock efficiently.",
    technologies: ["TypeScript"],
    category: "Full-Stack",
    difficulty: "Intermediate",
    codeUrl: "https://github.com/DripCode-Studio/stocky",
    thumbnail: "/images/projects/stocky.png",
    status: "Archived",
    features: [
      "Inventory Tracking: Monitor stock levels in real time.",
      "Stock Management: Add, update, and delete inventory items.",
      "Organizational Tools: Categorize and organize stock for easy access.",
      "Alerts and Notifications: Get notified when stock levels are low or when items need restocking.",
    ],
  },
  {
    id: uuidv4(),
    title: "TIL : Today I Learned",
    description:
      "A Today I Learned repository for capturing short learning notes, keeping technical discoveries organized and easy to revisit.",
    technologies: ["Markdown", "Quartz", "Obsidian"],
    category: "Learning",
    difficulty: "Beginner",
    codeUrl: "https://github.com/Lil-Code30/TIL",
    demoUrl: "https://til-journal.vercel.app/",
    thumbnail: "/images/projects/til.png",
    status: "Archived",
    features: [
      "Short learning notes and discoveries.",
      "Markdown-based knowledge collection.",
      "Easy reference for future study.",
    ],
  },
  {
    id: uuidv4(),
    title: "React+Firebase",
    description:
      "A React and Firebase starter project covering authentication, database reads and writes",
    technologies: ["React", "Firebase", "JavaScript"],
    category: "Full-Stack",
    difficulty: "Intermediate",
    codeUrl: "https://github.com/Lil-Code30/react-firebase",
    thumbnail: "/images/projects/react-firebase.png",
    status: "Completed",
    features: [
      "Firebase authentication.",
      "Google OAuth integration.",
      "Database read and write operations.",
    ],
  },
  {
    id: uuidv4(),
    title: "Random Color Toolkit",
    description:
      "This is a color manipulation toolkit for JavaScript applications. It provides a set of utilities for working with colors in various formats.",
    technologies: ["JavaScript"],
    category: "Package",
    difficulty: "Intermediate",
    codeUrl: "https://github.com/Lil-Code30/random-color-toolkit",
    demoUrl: "https://www.npmjs.com/package/random-color-toolkit",
    thumbnail: "/images/projects/random-color-toolkit.png",
    status: "Completed",
    features: [
      "Color Generation: Generate random colors in HEX, RGB, and HSL formats.",
      // "Color Conversion: Convert colors between HEX, RGB, and HSL formats.",
      // "Color Manipulation: Lighten, darken, and adjust the opacity of colors.",
      // "Color Validation: Validate color strings to ensure they are in a correct format.",
      "Comprehensive Documentation: Clear documentation for all functions and features.",
      "Unit Tests: Thorough unit tests to ensure reliability and correctness.",
      "Easy Integration: Simple API for easy integration into any JavaScript project.",
      "Open Source: Available on GitHub for contributions and improvements.",
      "NPM Package: Published on NPM for easy installation and usage.",
    ],
  },
  {
    id: uuidv4(),
    title: "Drip On Drip Dashboard",
    description:
      "This is the official dashboard for the Drip On Drip e-commerce platform. It provides administrative and analytical tools to manage the online store.",
    technologies: [
      "React",
      "Next.js",
      "TypeScript",
      "Vite",
      "TailwindCSS",
      "Shadcn/ui",
    ],
    category: "Full-Stack",
    difficulty: "Advanced",
    codeUrl: "https://github.com/Lil-Code30/dripOndrip-dashboard",
    demoUrl: "https://drip-ondrip-dashboard.vercel.app/",
    thumbnail: "/images/projects/drip-ondrip-dashboard.png",
    status: "Paused",
    features: [
      "User Authentication: Secure login for administrators.",
      "Dashboard Overview: Key metrics and statistics at a glance.",
      "Product Management: Add, update, and delete products.",
      "Order Management: View and manage customer orders.",
      "User Management: Manage customer accounts and permissions.",
      "Analytics: Visualize sales data and customer behavior.",
      "Responsive Design: Mobile-friendly layout using TailwindCSS.",
      "Component-Based Architecture: Built with reusable React components.",
      "State Management: Efficient state handling with React Query.",
      "Comprehensive Documentation: Clear documentation for setup and usage.",
      "Environment Variable Management: Secure handling of sensitive information using dotenv.",
    ],
  },
  {
    id: uuidv4(),
    title: "DripOnDrip - E-commerce Website for Clothing",
    description:
      "DripOnDrip is a modern, full-stack e-commerce web application for clothing. It provides a seamless shopping experience with a user-friendly interface and robust backend.",
    technologies: [
      "React",
      "JavaScript",
      "Vite",
      "TailwindCSS",
      "React Query",
      "Node.js",
      "Prisma",
      "Express",
      "PostgreSQL",
      "Neon",
      "JWT",
      "RESTful API",
      "Stripe",
      "Nodemailer",
    ],
    category: "Full-Stack",
    difficulty: "Advanced",
    codeUrl: "https://github.com/Lil-Code30/drip-on-drip_e-com",
    demoUrl: "https://drip-on-drip-v1.vercel.app/shop",
    thumbnail: "/images/projects/drip-on-drip-v1.png",
    status: "Completed",
    features: [
      "Responsive Design: Mobile-friendly design using TailwindCSS.",
      "State Management: Efficient state management using React Query.",
      "Product Catalog: Browse and search for products with filtering options.",
      "Shopping Cart: Add, remove, and update product quantities in the cart.",
      "User Authentication: Secure user registration and login using JWT (JSON Web Tokens).",
      "Order Processing: Create and manage orders, including order status updates and order history.",
      "Payment Integration: Seamless payment processing using Stripe API.",
      "Email Notifications: Automated email notifications for order confirmations and updates using Nodemailer.",
      "Robust Error Handling: Comprehensive error handling and validation for all user inputs.",
      "Backend API: RESTful API built with Express.js and Prisma ORM for database interactions.",
      "Database Management: Efficient data storage and retrieval using PostgreSQL with Prisma ORM.",
      "Comprehensive Documentation: Clear and detailed documentation for both frontend and backend components.",
      "Environment Variable Management: Secure management of sensitive information using dotenv.",
    ],
  },
  {
    id: uuidv4(),
    title: "DripOnDrip E-commerce Backend",
    description:
      "This is the backend for the DripOnDrip e-commerce platform. It is a Node.js application built with Express.js and Prisma. The API is live and hosted on Render, and the database is a PostgreSQL database hosted on Neon.",
    technologies: [
      "Node.js",
      "Prisma",
      "Express",
      "PostgreSQL",
      "Neon",
      "JWT",
      "RESTful API",
      "Stripe",
      "Nodemailer",
    ],
    category: "Backend",
    difficulty: "Advanced",
    codeUrl: "https://github.com/Lil-Code30/drip-on-drip_e-com_backend",
    demoUrl: "https://drip-on-drip-e-com-backend.onrender.com/api/v1/products",
    thumbnail: "/images/projects/drip-on-drip-e-com-backend.png",
    status: "Completed",
    features: [
      "User Authentication: Secure user registration and login using JWT (JSON Web Tokens).",
      "Product Management: CRUD operations for managing products, including adding, updating, and deleting products.",
      "Order Processing: Create and manage orders, including order status updates and order history.",
      "Payment Integration: Seamless payment processing using Stripe API.",
      "Email Notifications: Automated email notifications for order confirmations and updates using Nodemailer.",
      "Robust Error Handling: Comprehensive error handling and validation for all API endpoints.",
      "Database Management: Efficient data storage and retrieval using PostgreSQL with Prisma ORM.",
      "Comprehensive API Documentation: Clear and detailed documentation for all API endpoints.",
      "Environment Variable Management: Secure management of sensitive information using dotenv.",
    ],
  },
  {
    id: uuidv4(),
    title: "Next.js Todo App",
    description:
      "This is a simple Todo application built with Next.js, React, and Tailwind CSS. It allows you to create, manage, and track your daily tasks.",
    technologies: ["HTML", "TypeScript", "Next.js", "TailwindCSS", "Shadcn/ui"],
    category: "Frontend",
    difficulty: "Beginner",
    codeUrl: "https://github.com/Lil-Code30/todo-app_nextJS",
    demoUrl: "https://todo-app-next-js-ebon.vercel.app/",
    thumbnail: "/images/projects/todo-app-next-js.png",
    status: "Completed",
    features: [
      "Create Todos: Add new tasks with a title and description.",
      "View Todos: See a list of all your tasks.",
      "Toggle Todos: Mark tasks as complete or incomplete.",
      "Delete Todos: Remove tasks you no longer need.",
    ],
  },
  {
    id: uuidv4(),
    title: "My Portfolio v3",
    description:
      "This is the third version of my personal portfolio, built with modern web technologies. It showcases my skills, projects, and experience as a web developer.",
    technologies: [
      "HTML",
      "TypeScript",
      "React",
      "Vite",
      "TailwindCSS",
      "Shadcn/ui",
    ],
    category: "Frontend",
    difficulty: "Beginner",
    codeUrl: "https://github.com/Lil-Code30/who-am-i",
    demoUrl: "https://ismaelloko-v2.vercel.app/",
    thumbnail: "/images/projects/who-am-i.png",
    status: "Completed",
    features: [
      "Responsive Design: The portfolio is fully responsive and works on all devices.",
      "Modern UI: The UI is built with Shadcn/ui components, providing a clean and modern look. ",
      "Component-Based Architecture: The codebase is organized into reusable components, making it easy to maintain and scale.",
      "Responsive Navigation: A responsive navigation bar that adapts to different screen sizes.",
    ],
  },
  {
    id: uuidv4(),
    title: "Basic Ecom : Angular E-commerce",
    description:
      "A basic e-commerce web application built with Angular and Tailwind CSS, featuring product listings, a shopping cart.",
    technologies: ["Angular", "TypeScript", "TailwindCSS"],
    category: "Frontend",
    difficulty: "Beginner",
    codeUrl: "https://github.com/Lil-Code30/base-Ecom_angular",
    demoUrl: "https://base-ecom-angular.vercel.app/",
    thumbnail: "/images/projects/base-ecom-angular.png",
    status: "Completed",
    features: [
      "Product Listing: Browse a variety of products with images, descriptions, and prices.",
      "Shopping Cart: Add products to the cart, view cart contents, and remove items.",
      "Responsive Design: A responsive layout that works on all devices.",
      "Clean UI: A modern and user-friendly interface using Tailwind CSS.",
      "Component-Based Architecture: Built with Angular's component-based architecture for scalability and maintainability.",
    ],
  },
  {
    id: uuidv4(),
    title: "Dev Quotes ",
    description:
      "This is a full-stack web application built with the MERN stack that allows users to share and manage development-related quotes.",
    technologies: [
      "MongoDB",
      "Express",
      "React",
      "Node.js",
      "RESTful API",
      "JWT",
      "Mongoose",
      "bcrypt",
    ],
    category: "Full-Stack",
    difficulty: "Intermediate",
    codeUrl: "https://github.com/Lil-Code30/dev-quotes_fullstack-app",
    demoUrl: "https://dev-quotes-v1.vercel.app/",
    thumbnail: "/images/projects/dev-quotes_fullstack-app.png",
    status: "Completed",
    features: [
      "Secure user authentication using JWT (JSON Web Tokens).",
      "RUD Operations: Create, Read, Update, and Delete quotes.",
      "Like and Dislike: Engage with quotes by liking or disliking them.",
      "Responsive Design: A responsive layout that works on all devices.",
      "Error Handling: Proper error handling and user feedback.",
    ],
  },
  {
    id: uuidv4(),
    title: "NodeJS with MongoDB",
    description:
      "This is a simple NodeJS project that demonstrates how to connect to a MongoDB database using Mongoose.",
    technologies: ["Node.js", "Express", "MongoDB", "Mongoose"],
    category: "API",
    difficulty: "Intermediate",
    codeUrl: "https://github.com/Lil-Code30/nodeJs-with-mongoDB",
    demoUrl: "https://nodejs-with-mongoose-api.onrender.com/post",
    thumbnail: "/images/projects/nodejs-with-mongoose-api.png",
    status: "Completed",
    features: [
      "Connect to MongoDB using Mongoose",
      "Define a Mongoose schema and model",
      "Environment variable management with dotenv",
      "Comprehensive API documentation",
    ],
  },
  {
    id: uuidv4(),
    title: "Spiral Sounds : Online Classic Vinyl Store",
    description: "A web application for buying classic vinyl records.",
    technologies: ["Node.js", "Express", "RESTful API"],
    category: "Full-Stack",
    difficulty: "Intermediate",
    codeUrl:
      "https://github.com/Lil-Code30/online-classic-vinyl-store_express-app",
    demoUrl: "https://online-classic-vinyl-store-express-app.onrender.com/",
    thumbnail: "/images/projects/online-classic-vinyl-store.png",
    status: "Archived",
    features: [
      "Browse and search for vinyl records",
      "View detailed information about each record",
      "Add records to a shopping cart",
    ],
  },
  {
    id: uuidv4(),
    title: "Startup List API",
    description:
      "A RESTful API built with Node.js, Express, and MongoDB that provides information about various startups, including their names, descriptions, industries, and funding stages. The API supports CRUD operations and is designed for easy integration with frontend applications.",
    technologies: ["Node.js", "Express", "RESTful API"],
    category: "API",
    difficulty: "Beginner",
    codeUrl: "https://github.com/Lil-Code30/startups-list-api",
    demoUrl: "https://startups-list-api.onrender.com/api",
    thumbnail: "/images/projects/startups-list-api.png",
    status: "Completed",
    features: [
      "CRUD operations for managing startup data",
      "Filtering startups by industry and funding stage",
      "Error handling and validation",
      "Comprehensive API documentation",
      "Environment variable management with dotenv",
    ],
  },
  {
    id: uuidv4(),
    title: "Todo App",
    description:
      "A simple and intuitive Todo application built with React and styled with Tailwind CSS. It allows users to add, edit, delete, and mark tasks as complete, helping them stay organized and manage their daily activities effectively.",
    technologies: [
      "HTML",
      "JavaScript",
      "React",
      "Vite",
      "TailwindCSS",
      "DaisyUI",
    ],
    category: "Frontend",
    difficulty: "Beginner",
    codeUrl: "https://github.com/Lil-Code30/to-do-App",
    demoUrl: "https://to-do-app-v2-ten.vercel.app/",
    thumbnail: "/images/projects/to-do-app.png",
    status: "Completed",
    features: [
      "Add new tasks with a title and description",
      "Edit existing tasks",
      "Delete tasks",
      "Mark tasks as complete or incomplete",
      "Filter tasks by Category (Personal, Work, Shopping, Others)",
      "Responsive design for all devices",
      "Counter tasks per category",
    ],
  },
  {
    id: uuidv4(),
    title: "VanLife",
    description:
      "A website where you can find information about different vans and reserve them for your trip.",
    technologies: ["HTML", "CSS", "React", "Vite", "TailwindCSS"],
    category: "Full-Stack",
    difficulty: "Intermediate",
    codeUrl: "https://github.com/Lil-Code30/van-life",
    demoUrl: "https://vanlife-v1.netlify.app/vans",
    thumbnail: "/images/projects/van-life.png",
    status: "Completed",
    features: [
      "Fake full-stack application",
      "Browse and filter vans by type",
      "View detailed information about each van",
      "User authentication (login and signup)",
      "Reserve vans for specific dates",
      "Responsive design for all devices",
      "Clean and modern design",
      "Error handling and loading states",
      "Admin dashboard for managing vans and reservations",
    ],
  },
  {
    id: uuidv4(),
    title: "Assembly: Endgame",
    description:
      "Assembly: Endgame is a fun, interactive word guessing game built with React.js. Inspired by Hangman, the twist is: you're battling Assembly itself. With every incorrect guess, a programming language is wiped out from the digital universe. Your mission is to save as many languages as possible!",
    technologies: ["HTML", "CSS", "JavaScript", "React", "Vite", "TailwindCSS"],
    category: "Game",
    difficulty: "Intermediate",
    codeUrl: "https://github.com/Lil-Code30/assembly-endgame",
    demoUrl: "https://assembly-endgame-v1.vercel.app/",
    thumbnail: "/images/projects/assembly-endgame-v1.png",
    status: "Completed",
    features: [
      "Guess letters to save programming languages",
      "Visual representation of languages being wiped out",
      "Responsive design for all devices",
      "Clean and modern design",
      "Win detection and celebration animation",
    ],
  },
  {
    id: uuidv4(),
    title: "Tenzies Game",
    description:
      'An interactive dice game built with React.js. The goal of the game is simple but addictive: get all ten dice to show the same number. Click on dice to "freeze" them, reroll the others, and be strategic!',
    technologies: ["HTML", "CSS", "JavaScript", "React", "Vite", "TailwindCSS"],
    category: "Game",
    difficulty: "Intermediate",
    codeUrl: "https://github.com/Lil-Code30/tenzies-game",
    demoUrl: "https://tenzies-game-mu-nine.vercel.app/",
    thumbnail: "/images/projects/tenzies-game.png",
    status: "Completed",
    features: [
      "Roll dice to match all numbers",
      "Hold dice to keep their values",
      "Responsive design for all devices",
      "Clean and modern design",
      "Win detection and celebration animation",
    ],
  },
  {
    id: uuidv4(),
    title: "Tech Bytes Hub : A Tech Blog",
    description:
      "Tech Bytes Hub is a personal tech blog where I share my thoughts on the latest trends in technology, programming tutorials, and insights into the tech industry.",
    technologies: ["HTML", "CSS"],
    category: "Frontend",
    difficulty: "Beginner",
    codeUrl: "https://github.com/Lil-Code30/tech-bytes-hub",
    demoUrl: "https://techbyteshub.netlify.app/",
    thumbnail: "/images/projects/tech-bytes-hub.png",
    status: "Completed",
    features: [
      "Blog homepage with recent posts",
      "Individual blog post pages",
      "Categories and tags for easy navigation",
      "Responsive design for all devices",
      "Clean and modern design",
    ],
  },
  {
    id: uuidv4(),
    title: "Portfolio Personnel v2",
    description:
      "Welcome to version 2 of my personal portfolio. This project aims to present my background, skills, and web development projects in a clear, aesthetically pleasing, and responsive manner.",
    technologies: ["HTML", "CSS", "JavaScript", "vite", "TailwindCSS"],
    category: "Frontend",
    difficulty: "Intermediate",
    codeUrl: "https://github.com/Lil-Code30/mon-portfolio-v2",
    demoUrl: "https://ismael-loko-v2.vercel.app/",
    thumbnail: "/images/projects/mon-portfolio-v2.png",
    status: "Completed",
    features: [
      "Introduction section with a catchy banner",
      "'About' section to introduce the developer",
      "Project gallery with interactive previews",
      "Responsive design adapted to all screens",
    ],
  },
  {
    id: uuidv4(),
    title: "Weather App",
    description:
      "This project is carried out as part of the Full Stack Developer path of The Odin Project. It is a simple weather application built with HTML, CSS, and JavaScript. It allows users to get the real-time current weather for a city using the OpenWeatherMap API and the Visual Crossing Weather API.",
    technologies: ["HTML", "CSS", "JavaScript", "Webpack", "APIs"],
    category: "Frontend",
    difficulty: "Intermediate",
    codeUrl: "https://github.com/Lil-Code30/weather_app",
    demoUrl: "https://lil-code30.github.io/weather_app/",
    thumbnail: "/images/projects/weather_app.png",
    status: "Completed",
    features: [
      "Search for current weather by city name",
      "Display temperature, weather conditions, humidity, and wind speed",
      "Toggle between Celsius and Fahrenheit",
      "Responsive design for various devices",
      "Clean and modern design",
      "Dynamic weather icons based on conditions",
      "Error handling for invalid city names",
      "Utilizes two different weather APIs for reliability",
    ],
  },
  {
    id: uuidv4(),
    title: "ToDo List",
    description:
      "A simple ToDo list application built with HTML, CSS, and JavaScript.",
    technologies: ["HTML", "CSS", "JavaScript", "Webpack", "localStorage"],
    category: "Frontend",
    difficulty: "Intermediate",
    codeUrl: "https://github.com/Lil-Code30/To-Do_List",
    demoUrl: "https://lil-code30.github.io/To-Do_List/",
    thumbnail: "/images/projects/To-Do_List.png",
    status: "Completed",
    features: [
      "Add, edit, and delete tasks",
      "Mark tasks as complete or incomplete",
      "Filter tasks by category (all, work, personal, etc.)",
      "Persist tasks using localStorage",
      "Responsive design for various devices",
      "Clean and modern design",
    ],
  },
  {
    id: uuidv4(),
    title: "Restaurant Page : Dev-Foods",
    description:
      "This project was created as part of The Odin Project’s Full Stack Developer path. It is a restaurant website with content managed using JavaScript.",
    technologies: ["HTML", "CSS", "JavaScript"],
    category: "Frontend",
    difficulty: "Intermediate",
    codeUrl: "https://github.com/Lil-Code30/Restaurant-Page",
    demoUrl: "https://lil-code30.github.io/Restaurant-Page/",
    thumbnail: "/images/projects/Restaurant-Page.png",
    status: "Completed",
    features: [
      "Dynamic content loading with JavaScript",
      "User-friendly interface",
      "Menu display with categories",
      "Clean and modern design",
    ],
  },
  {
    id: uuidv4(),
    title: "Tic Tac Toe Game",
    description:
      "A classic Tic Tac Toe game built with HTML, CSS, and JavaScript. Play against a friend in a two-player mode.",
    technologies: ["HTML", "CSS", "JavaScript"],
    category: "Game",
    difficulty: "Beginner",
    codeUrl: "https://github.com/Lil-Code30/Tic-Tac-Toe_Game",
    demoUrl: "https://lil-code30.github.io/Tic-Tac-Toe_Game/",
    thumbnail: "/images/projects/Tic-Tac-Toe_Game.png",
    status: "Completed",
    features: [
      "Two-player mode",
      "Responsive design for various devices",
      "User-friendly interface",
      "Win detection and draw scenarios",
      "Clean and modern design",
    ],
  },
  {
    id: uuidv4(),
    title: " eLibrary : virtual library",
    description:
      "This project was created as part of The Odin Project’s Full Stack Developer path. It is a virtual library that allows adding, deleting, and managing books with JavaScript.",
    technologies: ["HTML", "CSS", "JavaScript"],
    category: "Frontend",
    difficulty: "Beginner",
    codeUrl: "https://github.com/Lil-Code30/eLibrary",
    demoUrl: "https://lil-code30.github.io/eLibrary/",
    thumbnail: "/images/projects/elibrary.png",
    status: "Completed",
    features: [
      "Responsive design for various devices",
      "Reading and unreading functionality",
      "User-friendly interface",
      "CRUD operations for managing books",
      "Clean and modern design",
    ],
  },
  {
    id: uuidv4(),
    title: "Netflix Clone : Home Page",
    description: "A clone of the Netflix home page streaming platform",
    technologies: ["HTML", "CSS"],
    category: "Frontend",
    difficulty: "Beginner",
    codeUrl: "https://github.com/Lil-Code30/netflix-clone-v1",
    thumbnail: "/images/projects/netflix-clone-v1.png",
    status: "Archived",
    features: [
      "Responsive layout",
      "Navigation bar with logo and search",
      "Hero section with background image",
    ],
  },
  {
    id: uuidv4(),
    title: "BISTRO Menu Page",
    description: 'Resto menu of "BISTRO BEEF & RESTO"',
    technologies: ["HTML", "CSS"],
    category: "Frontend",
    difficulty: "Beginner",
    codeUrl: "https://github.com/Lil-Code30/Bistro_menupage",
    demoUrl: "https://lil-code30.github.io/Bistro_menupage/",
    thumbnail: "/images/projects/bistro-menu-page.png",
    status: "Completed",
    features: [
      "Header with logo and contact information",
      "Menu categories (Starters, Main Courses, Desserts, Beverages)",
      "Mostly suitable for desktop view",
      "Clean and modern design",
    ],
  },
  {
    id: uuidv4(),
    title: "Limos : Transportation Booking",
    description: "Limos is a transportation booking platform. Just Frontend.",
    technologies: ["HTML", "CSS"],
    category: "Frontend",
    difficulty: "Beginner",
    codeUrl: "https://github.com/Lil-Code30/LIMOS-site-web_HTML-CSS",
    demoUrl: "https://limos.vercel.app/",
    thumbnail: "/images/projects/limos.png",
    status: "Completed",
    features: [
      "Homepage with hero section",
      "Services overview",
      "Booking form",
      "Contact information",
      "Responsive design for all devices",
    ],
  },
  {
    id: uuidv4(),
    title: "Portfolio Personnel v1",
    description:
      "Personal Portfolio – A project developed as part of the Responsive Web Design certification from freeCodeCamp.",
    technologies: ["HTML", "CSS"],
    category: "Frontend",
    difficulty: "Beginner",
    codeUrl: "https://github.com/Lil-Code30/mon-portfolio-v1",
    demoUrl: "https://ismael-loko-v1.vercel.app/",
    thumbnail: "/images/projects/mon-portfolio-v1.png",
    status: "Completed",
    features: [
      "Introduction section with a catchy banner",
      "'About' section to introduce the developer",
      "Project gallery with interactive previews",
      "Responsive design adapted to all screens",
    ],
  },
  {
    id: uuidv4(),
    title: "Multishop eCommerce Platform",
    description:
      "An eCommerce web application built with Django, designed for multimedia products like phones, shoes, computers, and more.",
    technologies: ["Django", "Python", "SQLite", "HTML", "CSS", "Bootstrap"],
    category: "Full-Stack",
    difficulty: "Intermediate",
    codeUrl: "https://github.com/Lil-Code30/django-ecommerce",
    thumbnail: "/images/projects/django-ecommerce.png",
    status: "Paused",
    features: [
      "Product catalog with categories",
      "Dynamic homepage displaying featured and recent products",
      "Admin dashboard for managing products, categories, and orders",
      "Responsive design for all device types",
    ],
  },
  {
    id: uuidv4(),
    title: "Django CRUD Application",
    description:
      "This is a simple Django-based web application that demonstrates basic CRUD (Create, Read, Update, Delete) operations. The app is designed to manage records, such as entries of a new employee (Full name, Email, Telephone, ...), in an easy-to-use web interface.",
    technologies: ["Django", "Python", "SQLite", "HTML", "CSS", "Bootstrap"],
    category: "Full-Stack",
    difficulty: "Beginner",
    codeUrl: "https://github.com/Lil-Code30/django-crud-app",
    thumbnail: "/images/projects/django-crud-app.png",
    status: "Completed",
    features: [
      "Create, Read, Update, Delete operations",
      "User-friendly interface",
      "Form validation",
      "Responsive design",
      "SQLite database integration",
    ],
  },
];
